import React, { useState } from 'react'
import { Card, Button, Row, Col, Badge } from 'react-bootstrap'
import {
  useFetchModelsQuery,
  useGetModelQuery,
  useRunBatchInferenceMutation,
} from '../../services/modelsApi'
import { selectSelectedModelId, clearSelectedModelId } from '../../store/slices/modelSlice'
import { rateBBoxModel, rateClassificationModel } from '../../utils'
import StarRating from '../StarRating'
import { Model } from '../../types/models/model'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectSelectedProjectId } from '../../store/slices/projectSlice'
import SelectedDatasetsCard from '../SelectedDatasetsCard'
import { selectDatasetSources } from '../../store/slices/datasetSlice'
import { useGetProjectQuery } from '../../services/projectsApi'
import ErrorDialog from '../modals/ErrorDialog'

interface ModelAssistedLabelingProps {
  onHide: () => void
}

const ModelAssistedLabeling: React.FC<ModelAssistedLabelingProps> = ({ onHide }) => {
  const dispatch = useAppDispatch()
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const selectedModelId = useAppSelector(selectSelectedModelId)
  const { data: selectedModel } = useGetModelQuery(selectedModelId || '', {
    skip: !selectedModelId,
  })

  const selectedDatasets = useAppSelector(selectDatasetSources)

  const {
    data: modelsData,
    isLoading,
    error,
  } = useFetchModelsQuery({ project_id: selectedProjectId || '' }, { skip: !selectedProjectId })
  const [runBatchInference, { isLoading: isBatchRunning }] = useRunBatchInferenceMutation()
  const [batchStatusLabel, setBatchStatusLabel] = useState<string>('Start')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [showError, setShowError] = useState(false)

  const handleApplyToProject = async (modelId: string) => {
    try {
      const datasource =
        selectedDatasets && selectedDatasets.length > 0
          ? { datasource: selectedDatasets }
          : undefined
      setBatchStatusLabel('Starting...')
      await runBatchInference({
        id: modelId,
        project_id: selectedProjectId || '',
        dataset_id: project?.datasetid || '',
        thumbnail_size: 640,
        metadata_filter: datasource,
        threshold: 0.1,
      }).unwrap()
      setTimeout(() => {
        onHide()
      }, 10000)
    } catch (error: any) {
      setErrorMessage(error?.data?.message || 'Failed to run batch inference')
      setShowError(true)
    }
  }

  const convertTimeFormat = (isoString: string | undefined): string => {
    if (!isoString) return 'N/A' // Handle undefined or null values
    const date = new Date(isoString) // Convert the string to a Date object
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true, // Use 12-hour format (AM/PM)
    })
  }

  const bBoxRating = (model: Model) => rateBBoxModel(model)
  const classificationRating = (model: Model) => rateClassificationModel(model)

  const handleSelectModelId = (model: Model) => {
    // Toggle selection of model (if already selected, deselect)
    if (selectedModel?.id === model.id) {
      dispatch(clearSelectedModelId(selectedProjectId || '')) // Deselect if the same model is clicked
    } else {
      // No need to set selected model // Select the model
    }
  }

  if (isLoading) return <div>Loading models...</div>
  if (error) return <div>Error loading models</div>

  return (
    <div className="model-assisted-labeling-list">
      <Row>
        <Col xs={7}>
          <Card
            style={{
              minHeight: '600px',
              maxHeight: '600px',
              padding: '10px',
              backgroundColor: '#333333',
            }}
          >
            <SelectedDatasetsCard />

            {/*<DatasetSourceDropdown forTraining={true} forUpload={false} disabled={false} />*/}

            {modelsData?.models
              ?.filter((model: Model) => model.deployment?.status.toLowerCase() === 'in_service')
              .map((model: Model) => (
                <Card
                  key={model.id}
                  className={`model-card mb-3 ${model.id === selectedModel?.id ? 'active' : ''}`}
                  onClick={() => handleSelectModelId(model)}
                >
                  <Card.Body style={{ padding: 0 }}>
                    <div className="model-card-header">
                      <div className="model-banner">
                        <span>{model.name}</span>
                      </div>

                      {model.state.toLowerCase() === 'trained' && (
                        <div className="star-rating-container">
                          <div className="rating-label">Rating</div>
                          {project?.annotation_type === 'classification' && (
                            <StarRating rating={classificationRating(model).classificationRating} />
                          )}
                          {project?.annotation_type === 'bounding_box' && (
                            <StarRating rating={bBoxRating(model).bBoxRating} />
                          )}
                        </div>
                      )}
                    </div>

                    <div className="d-flex justify-content-between align-items-center">
                      <div className="model-card-date">
                        {new Date(model.created_at).toLocaleString()}
                      </div>
                    </div>

                    <Row className="d-flex justify-content-between align-items-center">
                      <Col>
                        <div className="model-state-container d-flex align-items-center">
                          {model.state.toLowerCase() === 'training' && (
                            <Badge pill className="model-badge badge-training">
                              Training
                            </Badge>
                          )}
                          {model.state.toLowerCase() === 'trained' &&
                            model.deployment?.status.toLowerCase() === 'creating' && (
                              <Badge pill className="model-badge badge-deploying">
                                Deploying
                              </Badge>
                            )}
                          {model.state.toLowerCase() === 'trained' &&
                            model.deployment?.status.toLowerCase() === 'in_service' && (
                              <Badge pill className="model-badge badge-deployed">
                                Deployed
                              </Badge>
                            )}
                          {model.state.toLowerCase() === 'trained' &&
                            model.deployment?.status.toLowerCase() === 'deleted' && (
                              <Badge pill className="model-badge badge-training">
                                Trained
                              </Badge>
                            )}
                        </div>
                      </Col>

                      <Col className="d-flex justify-content-end">
                        {model.metrics &&
                          model.metrics['validation:mAP'] !== undefined &&
                          model.metrics['validation:mAP'] !== null && (
                            <div className="model-map-box">
                              <div className="model-map-label">mAP</div>
                              <div className="model-map-value">
                                {`${Math.ceil(Number(model.metrics['validation:mAP']) * 100)}%`}
                              </div>
                            </div>
                          )}
                        {model.metrics &&
                          model.metrics['train:smooth_l1'] !== undefined &&
                          model.metrics['validation:mAP'] !== null && (
                            <div className="model-map-box">
                              <div className="model-map-label">L1</div>
                              <div className="model-map-value">
                                {`${Math.ceil(Number(model.metrics['train:smooth_l1']) * 100)}%`}
                              </div>
                            </div>
                          )}
                        {model.metrics &&
                          model.metrics['train:cross_entropy'] !== undefined &&
                          model.metrics['validation:mAP'] !== null && (
                            <div className="model-map-box">
                              <div className="model-map-label">Loss</div>
                              <div className="model-map-value">
                                {`${Math.ceil(
                                  Number(model.metrics['train:cross_entropy']) * 100
                                )}%`}
                              </div>
                            </div>
                          )}
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}
          </Card>
        </Col>

        {/* Button should always render if a model is selected */}
        <Col xs={5}>
          <Card
            style={{
              height: '300px',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'transparent',
              border: 'none',
            }}
          >
            {selectedModel && (
              <div className="suggestive-label-button">
                <Button
                  className={`suggestive-label-button-inner ${
                    isBatchRunning || selectedModel.batch?.status.toLowerCase() === 'running'
                      ? 'training'
                      : ''
                  }`}
                  onClick={() => handleApplyToProject(selectedModel.id)}
                  disabled={
                    isBatchRunning || selectedModel.batch?.status.toLowerCase() === 'running'
                  }
                >
                  {batchStatusLabel}
                </Button>
              </div>
            )}
          </Card>
          <Card
            style={{
              height: '300px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'transparent',
              border: 'none',
            }}
          >
            {selectedModel && (
              <div className="suggestive-label-card">
                <div className="batch-info">
                  <Row className="info-row">
                    <Col>
                      <strong>Started At:</strong>
                    </Col>
                    <Col>
                      <span>{convertTimeFormat(selectedModel.batch?.started_at) || 'N/A'}</span>
                    </Col>
                  </Row>
                  <Row className="info-row">
                    <Col>
                      <strong>Ended At:</strong>
                    </Col>
                    <Col>
                      <span>{convertTimeFormat(selectedModel.batch?.ended_at) || 'N/A'}</span>
                    </Col>
                  </Row>
                  <Row className="info-row">
                    <Col>
                      <strong>Threshold:</strong>
                    </Col>
                    <Col>
                      <span>{selectedModel.batch?.threshold * 100 + '%' || 'N/A'}</span>
                    </Col>
                  </Row>
                  <Row className="info-row">
                    <Col>
                      <strong>Images:</strong>
                    </Col>
                    <Col>
                      <span>{selectedModel.batch?.completed_content || 0}</span>
                    </Col>
                  </Row>
                </div>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <ErrorDialog
        show={showError}
        onHide={() => setShowError(false)}
        message={errorMessage}
        title="Batch AAL Error"
      />
    </div>
  )
}

export default ModelAssistedLabeling
